import { useSyncExternalStore } from 'react'

/**
 * EVERY REPLY, OR ONLY THE LAST — owner, 2026-10-08: 只要是 agent 他吐出來的對話, 就選擇
 * 不要折 … 在 Show The Working 後面, 加上一個開關. 這個開關預設是開啟的, 用來顯示所有純對話
 * 的訊息. 當這個開關關閉的時候, 就只會顯示最後一個對話.
 *
 * What a folded turn keeps on screen. ON (the default): everything the agent SAID that
 * turn, in order — only the tool calls and the thinking stay behind the fold. OFF: the
 * last thing it said and nothing else, which is what Clean View did until this day.
 *
 * WHY THE DEFAULT MOVED. "Only the last" assumed a turn ends with its one conclusion.
 * A turn is everything between two messages TO the agent, and plenty wakes an agent
 * that is not one — another session's message, a `!` command — so a reply that had
 * FINISHED became "the working" the moment the agent went on, and vanished before it
 * was read (a colleague's report the same day: every summary buried by the next
 * background agent reporting in). Showing what was said cannot bury what was said.
 *
 * ONE ANSWER FOR THE WHOLE VIEW, kept on this device. The switch is drawn on each turn
 * that has something for it to show, but it is a way of reading, not a property of one
 * turn: a person who wants the short form wants it everywhere, and would otherwise be
 * pressing it once per turn. A module store (the shape of search/searchStore.ts), so
 * every turn on screen follows the one that was pressed.
 *
 * Stored only when OFF: the absence of the key is the default, so a default that moves
 * again later moves for everyone who never chose.
 */
const KEY = 'av_clean_every_reply'

function read(): boolean {
  try {
    return localStorage.getItem(KEY) !== '0'
  } catch {
    return true // storage refused (private mode, a webview without it): the default
  }
}

let on = read()
const listeners = new Set<() => void>()

function publish(next: boolean): void {
  if (next === on) return
  on = next
  for (const l of [...listeners]) l()
}

export function everyReply(): boolean {
  return on
}

export function setEveryReply(next: boolean): void {
  try {
    if (next) localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, '0')
  } catch {
    /* not remembered past this page; still applied to it */
  }
  publish(next)
}

function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** Whether a folded turn shows every reply. Re-renders the caller when it changes. */
export function useEveryReply(): boolean {
  return useSyncExternalStore(subscribe, everyReply, () => true)
}

// A second window of the same app on this device follows the first.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY || e.key === null) publish(read())
  })
}

/** Tests only: back to what a fresh page reads from storage. */
export function __rereadEveryReply(): void {
  publish(read())
}
